import React, { createContext, useCallback, useState, useContext, useRef } from 'react';
import ItemService from 'services/ItemService';
import VariantService from 'services/VariantService';
import { AlertMessagesContext } from 'react-alert-messages';
import { ITEM_TYPES } from 'const';
import PriceCategoriesService from 'services/PriceCategoryService';
import ItemAddonService from 'services/ItemAddonService';
import ChargeServices from 'services/ChargeServices';

export const InventoryContext = createContext();

function InventoryProvider({ children }) {
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState(null);
  const [variantLabels, setVariantLabels] = useState([]);
  const [variantDimensions, setVariantDimensions] = useState([]);
  const [productVariantMapping, setProductVariantMapping] = useState([]);
  const [baseProducts, setBaseProducts] = useState([]);
  const [priceCategories, setPriceCategories] = useState(null);
  const [itemPriceCategories, setItemPriceCategories] = useState([]);
  const [itemAddonMappings, setItemAddonMappings] = useState([]);
  const [chargeGroups, setChargeGroups] = useState([]);
  const [charges, setCharges] = useState([]);
  const [stockBatches, setStockBatches] = useState([]);

  const productsCacheRef = useRef(null);
  const categoriesCacheRef = useRef(null);
  const variantLabelsCacheRef = useRef(null);
  const variantDimensionsCacheRef = useRef(null);
  const productVariantMappingCacheRef = useRef(null);
  const baseProductsCacheRef = useRef(null);
  const priceCategoriesCacheRef = useRef(null);
  const itemPriceCategoriesCacheRef = useRef(null);
  const itemAddonMappingsCacheRef = useRef(null);
  const chargeGroupsCacheRef = useRef(null);
  const chargesCacheRef = useRef(null);
  const stockBatchesCacheRef = useRef(null);

  const fetchData = useCallback(
    async (fetchFunction, { params = {} } = {}) => {
      try {
        if (params.paginated) {
          const limit = params.limit || 100;
          let offset = params.offset || 0;
          let allData = [];

          while (true) {
            const { data, count } = await fetchFunction({
              offset,
              limit,
              ...params.extra,
            });
            allData = [...allData, ...data];
            if (allData.length >= count) break;
            offset += limit;
          }

          return allData;
        }

        const resp = await fetchFunction(params.extra || {});
        return resp;
      } catch (error) {
        postAlertMessage?.({
          text: error.message || 'Failed to fetch data',
          type: 'failed',
          timeout: 5000,
        });
        return [];
      }
    },
    [postAlertMessage]
  );

  const fetchProducts = useCallback(
    () =>
      fetchData(ItemService.getItemsResponse, {
        params: {
          paginated: true,
          limit: 500,
          extra: {
            type: `${ITEM_TYPES.INVENTORY},${ITEM_TYPES.COMBO},${ITEM_TYPES.SERVICE},${ITEM_TYPES.PRODUCTION}`,
          },
        },
      }),
    [fetchData]
  );

  const fetchCategories = useCallback(
    () => fetchData(ItemService.getItemCategories, { params: { extra: {} } }),
    [fetchData]
  );

  const fetchPriceCategories = useCallback(
    () => fetchData(PriceCategoriesService.getPriceCategories, { params: { extra: {} } }),
    [fetchData]
  );

  const fetchItemPriceCategories = useCallback(
    () => fetchData(PriceCategoriesService.getItemPriceCategories, { params: { extra: {} } }),
    [fetchData]
  );

  const fetchItemAddonMappings = useCallback(
    () => fetchData(ItemAddonService.getItemAddonMappings, { params: { extra: {} } }),
    [fetchData]
  );

  const fetchChargeGroups = useCallback(
    () => fetchData(ChargeServices.getChargeGroups, { params: { extra: {} } }),
    [fetchData]
  );

  const fetchCharges = useCallback(() => fetchData(ChargeServices.getCharges, { params: { extra: {} } }), [fetchData]);

  const fetchVariantLabels = useCallback(
    () =>
      fetchData(VariantService.getVariantLabels, setVariantLabels, {
        params: { extra: {} },
      }),
    [fetchData]
  );

  const fetchVariantDimensions = useCallback(
    () =>
      fetchData(VariantService.getVariantDimensions, setVariantDimensions, {
        params: { extra: {} },
      }),
    [fetchData]
  );

  const fetchProductVariantMapping = useCallback(
    () =>
      fetchData(VariantService.getProductVariantMappings, setProductVariantMapping, {
        params: { extra: {} },
      }),
    [fetchData]
  );

  const fetchBaseProducts = useCallback(
    () =>
      fetchData(VariantService.getBaseProducts, setBaseProducts, {
        params: { extra: {} },
      }),
    [fetchData]
  );

  const fetchStockBatches = useCallback(
    () => fetchData(ItemService.getStockBatches, { params: { extra: {} } }),
    [fetchData]
  );

  const getProducts = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && productsCacheRef.current) {
        return productsCacheRef.current;
      }
      const _products = await fetchProducts();
      productsCacheRef.current = _products;
      setProducts(_products);
      return _products;
    },
    [fetchProducts]
  );

  const getCategories = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && categoriesCacheRef.current) {
        return categoriesCacheRef.current;
      }
      const _categories = await fetchCategories();
      categoriesCacheRef.current = _categories;
      setCategories(_categories);
      return _categories;
    },
    [fetchCategories]
  );

  const getPriceCategories = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && priceCategoriesCacheRef.current) {
        return priceCategoriesCacheRef.current;
      }
      const _data = await fetchPriceCategories();
      priceCategoriesCacheRef.current = _data;
      setPriceCategories(_data);
      return _data;
    },
    [fetchPriceCategories]
  );

  const getItemPriceCategories = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && itemPriceCategoriesCacheRef.current) {
        return itemPriceCategoriesCacheRef.current;
      }
      const _data = await fetchItemPriceCategories();
      itemPriceCategoriesCacheRef.current = _data;
      setItemPriceCategories(_data);
      return _data;
    },
    [fetchItemPriceCategories]
  );

  const getItemAddonMappings = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && itemAddonMappingsCacheRef.current) {
        return itemAddonMappingsCacheRef.current;
      }
      const _data = await fetchItemAddonMappings();
      itemAddonMappingsCacheRef.current = _data;
      setItemAddonMappings(_data);
      return _data;
    },
    [fetchItemAddonMappings]
  );

  const getChargeGroups = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && chargeGroupsCacheRef.current) {
        return chargeGroupsCacheRef.current;
      }
      const _data = await fetchChargeGroups();
      chargeGroupsCacheRef.current = _data;
      setChargeGroups(_data);
      return _data;
    },
    [fetchChargeGroups]
  );

  const getCharges = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && chargesCacheRef.current) {
        return chargesCacheRef.current;
      }
      const _data = await fetchCharges();
      chargesCacheRef.current = _data;
      setCharges(_data);
      return _data;
    },
    [fetchCharges]
  );

  const getVariantLabels = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && variantLabelsCacheRef.current) {
        return variantLabelsCacheRef.current;
      }
      const _variantLabels = await fetchVariantLabels();
      variantLabelsCacheRef.current = _variantLabels;
      setVariantLabels(_variantLabels);
      return _variantLabels;
    },
    [fetchVariantLabels, setVariantLabels]
  );

  const getVariantDimensions = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && variantDimensionsCacheRef.current) {
        return variantDimensionsCacheRef.current;
      }
      const _variantDimensions = await fetchVariantDimensions();
      variantDimensionsCacheRef.current = _variantDimensions;
      setVariantDimensions(_variantDimensions);
      return _variantDimensions;
    },
    [fetchVariantDimensions, setVariantDimensions]
  );

  const getProductVariantMapping = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && productVariantMappingCacheRef.current) {
        return productVariantMappingCacheRef.current;
      }
      const _productVariantMapping = await fetchProductVariantMapping();
      productVariantMappingCacheRef.current = _productVariantMapping;
      setProductVariantMapping(_productVariantMapping);
      return _productVariantMapping;
    },
    [fetchProductVariantMapping, setProductVariantMapping]
  );

  const getBaseProducts = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && baseProductsCacheRef.current) {
        return baseProductsCacheRef.current;
      }
      const _baseProducts = await fetchBaseProducts();
      baseProductsCacheRef.current = _baseProducts;
      setBaseProducts(_baseProducts);
      return _baseProducts;
    },
    [fetchBaseProducts, setBaseProducts]
  );

  const getStockBatches = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && stockBatchesCacheRef.current) {
        return stockBatchesCacheRef.current;
      }
      const _data = await fetchStockBatches();
      stockBatchesCacheRef.current = _data;
      setStockBatches(_data || []);
      return _data;
    },
    [fetchStockBatches]
  );

  return (
    <InventoryContext.Provider
      value={{
        getProducts,
        getCategories,
        getVariantLabels,
        getVariantDimensions,
        getProductVariantMapping,
        getBaseProducts,
        getPriceCategories,
        getItemPriceCategories,
        getItemAddonMappings,
        getChargeGroups,
        getCharges,
        getStockBatches,
        products,
        categories,
        variantLabels,
        variantDimensions,
        productVariantMapping,
        baseProducts,
        priceCategories,
        itemPriceCategories,
        itemAddonMappings,
        chargeGroups,
        charges,
        stockBatches,
        setProducts,
        setCategories,
        setPriceCategories,
        setItemPriceCategories,
        setItemAddonMappings,
        setChargeGroups,
        setVariantLabels,
        setVariantDimensions,
        setBaseProducts,
        setProductVariantMapping,
        setCharges,
        setStockBatches,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export default InventoryProvider;
